import type { Point } from '@pn/core/domain/point';
import { hasKey } from '@pn/core/utils/logic';
import {
  isArray,
  isNumber,
  isObject,
  isPlainObject,
  isString,
  mapValues,
} from 'lodash-es';
import mapboxgl from 'mapbox-gl';

export function toMapboxPoint(point: Point): mapboxgl.Point {
  return new mapboxgl.Point(point.x, point.y);
}

export function toMapboxBBox(
  bbox: [Point, Point]
): [mapboxgl.Point, mapboxgl.Point] {
  return [toMapboxPoint(bbox[0]), toMapboxPoint(bbox[1])];
}

export function bBoxToMapboxBBox(
  mapboxMap: mapboxgl.Map,
  bbox: GeoJSON.BBox
): [mapboxgl.Point, mapboxgl.Point] {
  const sw = mapboxMap.project([bbox[0], bbox[1]]);
  const ne = mapboxMap.project([bbox[2], bbox[3]]);

  return [sw, ne];
}

export function isMapboxLayer(arg: unknown): arg is mapboxgl.Layer {
  return (
    isObject(arg) &&
    hasKey(arg, 'id') &&
    hasKey(arg, 'type') &&
    hasKey(arg, 'source')
  );
}

type Expr = unknown;

type Mode = 'scaleOutputs' | 'noScale';

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v);

const roundMul = (n: number, factor: number, min: number, decimals: number) => {
  const x = Math.max(n * factor, min);
  const p = 10 ** decimals;
  return Math.round(x * p) / p;
};

export function multiplyExpression(
  expression: Expr,
  factor: number,
  min = -Infinity,
  decimals = 2
): Expr {
  const walk = (expr: Expr, mode: Mode): Expr => {
    // numbers
    if (isFiniteNumber(expr) && isNumber(expr)) {
      return mode === 'scaleOutputs'
        ? roundMul(expr, factor, min, decimals)
        : expr;
    }

    // arrays
    if (isArray(expr)) {
      if (expr.length === 0) return expr;

      const op = expr[0];
      if (!isString(op)) return expr.map((v) => walk(v, mode));

      const scaleOrWalk = (v: unknown) =>
        isFiniteNumber(v) && isNumber(v)
          ? roundMul(v, factor, min, decimals)
          : walk(v, 'scaleOutputs');

      const noScaleWalk = (v: unknown) => walk(v, 'noScale');

      const mapStopValuePairs = (
        arr: unknown[],
        startIndex: number,
        stopMode: Mode,
        valueMode: Mode
      ) => {
        const out = [...arr];
        for (let i = startIndex; i < out.length; i++) {
          const isValue = (i - startIndex) % 2 === 1;
          out[i] = isValue ? walk(out[i], valueMode) : walk(out[i], stopMode);
        }
        return out;
      };

      switch (op) {
        case 'interpolate': {
          /**
           * ["interpolate", interpolation, input, stop1, val1, ...]
           * interpolation config: leave as-is
           */
          const out = [...expr];
          // input: noScale
          out[2] = noScaleWalk(out[2]);
          // stops: noScale, values: scaleOutputs
          return mapStopValuePairs(out, 3, 'noScale', 'scaleOutputs');
        }

        case 'step': {
          // ["step", input, base, stop1, val1, ...]
          const out = [...expr];
          out[1] = noScaleWalk(out[1]); // input
          out[2] = scaleOrWalk(out[2]); // base output
          return mapStopValuePairs(out, 3, 'noScale', 'scaleOutputs');
        }

        case 'match': {
          // ["match", input, label1, out1, ..., defaultOut]
          const out = [...expr];
          out[1] = noScaleWalk(out[1]); // input

          for (let i = 2; i < out.length; i++) {
            const isDefault = i === out.length - 1;
            const isOutput = isDefault || (i - 2) % 2 === 1;
            out[i] = isOutput
              ? walk(out[i], 'scaleOutputs')
              : walk(out[i], 'noScale');
          }
          return out;
        }

        case 'case': {
          // ["case", cond1, out1, cond2, out2, ..., defaultOut]
          const out = [...expr];

          for (let i = 1; i < out.length; i++) {
            const isDefault = i === out.length - 1;
            const isOutput = isDefault || (i - 1) % 2 === 1;
            out[i] = isOutput
              ? walk(out[i], 'scaleOutputs')
              : walk(out[i], 'noScale');
          }
          return out;
        }

        default:
          // Unknown operator: recurse with current mode (don’t throw)
          return expr.map((v) => walk(v, mode));
      }
    }

    // objects
    if (isPlainObject(expr)) {
      return mapValues(expr as Record<string, unknown>, (v) => walk(v, mode));
    }

    // strings/booleans/null/etc.
    return expr;
  };

  // top-level: treat numbers as outputs (scale)
  return walk(expression, 'scaleOutputs');
}

/**
 * Mapbox doesn't throw when a source or layer operation fails (e.g. validation
 * or a missing `before` layer); it fires an `error` event and silently skips
 * the operation. Run `fn` while collecting those errors and rethrow them with
 * Mapbox's actual reason.
 */
export function throwOnMapboxErrors(
  map: mapboxgl.Map,
  errorPrefix: string,
  fn: () => void
): void {
  const mapboxErrors: Error[] = [];
  const onError = (event: mapboxgl.ErrorEvent) =>
    mapboxErrors.push(event.error);

  map.on('error', onError);
  try {
    fn();
  } finally {
    map.off('error', onError);
  }

  if (mapboxErrors.length > 0) {
    throw new Error(
      `${errorPrefix}: ${mapboxErrors.map((e) => e.message).join('; ')}`
    );
  }
}
